import React, {ReactNode, useCallback, useEffect} from 'react';
import {some, isEmpty, sortBy, map, omit} from 'lodash';
import classNames from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {newTestScore, editTestScore} from 'shared/portfolio/actions/test-scores';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {Store} from 'src/types/store';
import TwoColumnMasonry from 'shared/portfolio/components/two-column-masonry';
import Card from '../card';
import PortfolioSectionHeader from '../portfolio-section-header';
import EmptyPlaceholder from '../empty-placeholder';
import TestScore from './test-score';
import TestScoreForm from 'shared/portfolio/components/test-scores/test-score-form';
import Section from '../section';
import PortfolioIcon from 'shared/portfolio/components/portfolio-icon/portfolio-icon';

const TestScores = () => {
  const mq = useMediaQuery();
  const dispatch = useDispatch();
  const testScores = useSelector((state: Store) => state.testScores);
  const editing = some(testScores, 'editing');
  const handleAddClick = useCallback(() => dispatch(newTestScore()), [dispatch]);

  useEffect(() => {
    window.Raise.vent.on('add:testscore', handleAddClick);
  }, [handleAddClick]);

  let testScoreItems: ReactNode = null;

  if (isEmpty(testScores)) {
    testScoreItems = (
      <div className="portfolio-sections column">
        <EmptyPlaceholder
          onClick={handleAddClick}
          header="Add A Test Score"
          description="Up to $1,000 per year"
          className="test-scores empty-placeholder"
          portfolioItemType="test-score"
        />
      </div>
    );
  } else {
    const scores = sortBy(testScores, (testScore) => {
      if (testScore.new) {
        return -Number.MAX_SAFE_INTEGER;
      }

      return -(testScore.year * 100 + testScore.month);
    });

    testScoreItems = (
      <TwoColumnMasonry>
        {map(scores, (testScore) => {
          if (testScore.editing) {
            return (
              <div
                key={testScore._id}
                className={classNames('portfolio-sections column', {
                  editing: testScore.editing,
                })}
              >
                <TestScoreForm testScore={{...omit(testScore, ['createdAt', 'updatedAt'])}} />
              </div>
            );
          } else {
            return (
              <div key={testScore._id} className="portfolio-sections column">
                <Card
                  id={testScore._id}
                  active={!!testScore.scrollTo}
                  onClick={() => dispatch(editTestScore(testScore))}
                >
                  <TestScore testScore={testScore} />
                </Card>
              </div>
            );
          }
        })}
      </TwoColumnMasonry>
    );
  }

  return (
    <div>
      <PortfolioSectionHeader
        title="Test Scores"
        addNew={handleAddClick}
        displayNew={!editing && !(mq.XS && !Object.keys(testScores).length)}
        sectionName="test-scores"
      >
        <PortfolioIcon type="testScores" size={mq.XS ? 40 : 56} />
      </PortfolioSectionHeader>
      <Section>
        <div id="scores" className="portfolio-sections main-section" />
        {testScoreItems}
      </Section>
    </div>
  );
};

export default TestScores;
